// Static renderer of ONE format variant, shared by the Studio canvas and the
// preview. Input is the canonical document; resolution comes from the Creative
// Engine (resolveVariant via variantViews), never from editor-only state:
// editor mode only ADDS affordances (hidden-in-editor layers skipped, layers
// invisible at load shown faintly, a transient gesture frame).
//
// Text is always rendered as text (React text nodes), never as HTML. This
// renders the STATIC presentation of a scene; animated values (transform,
// opacity at an instant) are written on top by render/animate.ts, and the
// preview's runtime state (show/hide, variables) arrives as props.

import {
  resolveVariant,
  type RunnerDecl,
  type AssetRef,
  type CreativeDocument,
  type Element,
  type Frame,
  type TextContent,
} from "@interactads/creative-model";
import { Fragment, type CSSProperties, type ReactNode, type Ref } from "react";
import { editingScene, findLayout, layoutSize, linkOfElement, variantViews, type ElementView } from "../core/model";
import type { GameElementView } from "../preview/engine";
import { gameLabel } from "@interactads/creative-runtime/ui-text";
import { runtimeLook } from "./runtime-state";
import {
  AudioContent,
  CarouselContent,
  CountdownContent,
  FieldContent,
  ProgressContent,
  SurveyQuestionContent,
  VideoContent,
  type ComponentHost,
  type EditorFocus,
} from "./components";
import { elementCss, fillCss, imageCss, shapeGeometry, type Css } from "./style";

export interface ArtboardProps {
  readonly doc: CreativeDocument;
  readonly layoutId: string;
  readonly assetUrl: (asset: AssetRef) => string;
  readonly loadedFonts?: ReadonlySet<string>;
  readonly mode: "editor" | "preview";
  /** Scene to draw (default: the initial scene). */
  readonly sceneId?: string;
  /** Root node handle for the animation driver. */
  readonly boardNode?: Ref<HTMLDivElement>;
  /** Preview only: visibility after show/hide actions. */
  readonly visibility?: ReadonlyMap<string, boolean>;
  /** Preview only: variable values for bound text runs. */
  readonly variables?: ReadonlyMap<string, number | boolean | string>;
  /** Preview only: draw hotspots (debug); they are invisible in the ad. */
  readonly showHotspots?: boolean;
  /** Preview only: pointer input routed to the preview engine. */
  readonly interact?: {
    readonly tappable: (id: string) => boolean;
    readonly hoverable: (id: string) => boolean;
    readonly tap: (id: string) => void;
    readonly hover: (id: string, enter: boolean) => void;
    // 7.6F: dragging (pointer) and its keyboard alternative (pick, then choose the zone).
    readonly draggable?: (id: string) => boolean;
    readonly dragStart?: (id: string) => boolean;
    readonly dragPosition?: (id: string, x: number, y: number) => { x: number; y: number };
    readonly dragEnd?: (id: string, x: number, y: number) => void;
    readonly pick?: (id: string) => void;
    readonly cancelPick?: () => void;
    // 7.6F.1: runner controls held down (duck) / pressed (jump), by pointer.
    readonly press?: (id: string, down: boolean) => void;
  };
  /** Preview only (7.6F): element states, runtime positions and game roles of the layers. */
  readonly runtime?: {
    readonly stateOf: (id: string) => string | undefined;
    readonly positionOf: (id: string) => { readonly x: number; readonly y: number } | undefined;
    readonly gameView: (id: string) => GameElementView | null;
    readonly picked: string | null;
    /**
     * 7.6F.1: the declared runner. Its spawn templates are drawn as a fixed pool of slots
     * (positions written per frame by render/runner-paint.ts), looping layers get a copy.
     */
    readonly runner?: RunnerDecl | null;
  };
  /** Editor only: layers hidden in the editor (authoring sidecar) are not drawn. */
  readonly editorHidden?: ReadonlySet<string>;
  /** Editor only: layers that ignore the pointer (locked). */
  readonly locked?: ReadonlySet<string>;
  /** Editor only: transient frame of the layer being dragged/resized (not yet an edit). */
  readonly gesture?: { readonly id: string; readonly frame: Frame } | null;
  /** Editor only: layer being edited inline (its text is drawn by the inline editor). */
  readonly editingText?: string | null;
  /** Preview only: a declared link was activated (the preview never navigates by itself). */
  readonly onActivateLink?: (linkId: string) => void;
  /** Preview only: live state and controls of advanced components (7.6E). */
  readonly host?: ComponentHost;
  /** Editor only: which carousel slide / survey question to show (ephemeral). */
  readonly focus?: EditorFocus;
  readonly className?: string;
}

const EMPTY = new Set<string>();

function bound(value: number | boolean | string | undefined, format: "integer" | "decimal1" | "mmss"): string {
  const n = typeof value === "number" ? value : 0;
  if (format === "decimal1") return n.toFixed(1);
  if (format === "mmss") {
    const total = Math.max(0, Math.round(n / 1_000));
    return `${String(Math.floor(total / 60))}:${String(total % 60).padStart(2, "0")}`;
  }
  return String(Math.round(n));
}

function Runs({ content, variables }: { content: TextContent; variables?: ArtboardProps["variables"] }) {
  return (
    <>
      {content.runs.map((run, i) => {
        if ("bind" in run) return <span key={i}>{bound(variables?.get(run.bind.var), run.bind.format)}</span>;
        const s = run.style;
        const css: CSSProperties = {
          ...(s?.weight !== undefined ? { fontWeight: s.weight } : {}),
          ...(s?.italic ? { fontStyle: "italic" } : {}),
          ...(s?.underline ? { textDecoration: "underline" } : {}),
          ...(s?.color ? { color: s.color } : {}),
        };
        return (
          <span key={i} style={css}>
            {run.text}
          </span>
        );
      })}
    </>
  );
}

/** Element types that draw their own controls: never wrapped as one big button (no nested controls). */
const OWN_CONTROLS = (el: Element) =>
  el.type === "audio" ||
  el.type === "field" ||
  el.type === "component" ||
  (el.type === "video" && (el.playback.controls ?? "none") !== "none");

interface RenderContext {
  readonly assetByKey: ReadonlyMap<string, AssetRef>;
  readonly resolved: ReturnType<typeof resolveVariant>;
}

function content(view: ElementView, props: ArtboardProps, rc: RenderContext): ReactNode {
  const { element: el, presentation: p } = view;
  const asset = (key: string | undefined) => (key === undefined ? undefined : rc.assetByKey.get(key));
  const url = (key: string | undefined) => {
    const a = asset(key);
    return a ? props.assetUrl(a) : undefined;
  };
  if (props.editingText === el.id) return null;
  switch (el.type) {
    case "text":
    case "button":
      return (
        <div>
          <Runs
            content={(p.text as unknown as TextContent | undefined) ?? { runs: [] }}
            {...(props.variables ? { variables: props.variables } : {})}
          />
        </div>
      );
    case "hotspot":
      // Never drawn in the ad: an outline only while editing, or in the preview's debug view.
      return props.mode === "editor" || props.showHotspots ? (
        <div
          className="ia-hotspot grid h-full w-full place-items-center text-[10px] font-medium"
          style={{ borderRadius: el.shape === "ellipse" ? "50%" : 0 }}
          data-hotspot-outline=""
        >
          {props.mode === "editor" ? (el.name ?? "Zona clicable") : null}
        </div>
      ) : null;
    case "image": {
      const a = asset(el.asset);
      return a ? <img src={props.assetUrl(a)} alt={el.alt} draggable={false} style={imageCss(el, p)} /> : null;
    }
    case "shape": {
      // Vector shapes (line / polygon / path): the SAME geometry as the served ad (style.ts).
      const g = shapeGeometry(el, p);
      if (!g) return null;
      const Tag = g.tag;
      return (
        <svg
          viewBox={g.viewBox}
          width="100%"
          height="100%"
          overflow="visible"
          aria-hidden="true"
          style={{ display: "block" }}
        >
          <Tag
            {...{ [g.attr]: g.value }}
            fill={g.fill}
            stroke={g.stroke}
            strokeWidth={g.strokeWidth}
            vectorEffect="non-scaling-stroke"
          />
        </svg>
      );
    }
    case "video":
      return (
        <VideoContent
          el={el}
          fit={(p.fit as string | undefined) ?? el.fit}
          mode={props.mode}
          url={url(el.asset)}
          posterUrl={url(el.poster)}
          captionsUrl={url(el.captions)}
          host={props.host}
        />
      );
    case "audio":
      return <AudioContent el={el} mode={props.mode} url={url(el.asset)} host={props.host} />;
    case "group":
      return el.children.map((id) => <Nested key={id} id={id} props={props} rc={rc} />);
    case "field": {
      const field = props.doc.forms?.find((f) => f.id === el.form)?.fields.find((f) => f.id === el.field);
      return <FieldContent el={el} field={field} mode={props.mode} host={props.host} />;
    }
    case "component":
      switch (el.component) {
        case "ia.carousel@1":
          return (
            <CarouselContent
              el={el}
              mode={props.mode}
              host={props.host}
              editorIndex={props.focus?.slide(el.id) ?? 0}
              renderSlide={(id) => <Nested id={id} props={props} rc={rc} fill />}
            />
          );
        case "ia.survey-question@1":
          return (
            <SurveyQuestionContent
              el={el}
              doc={props.doc}
              mode={props.mode}
              host={props.host}
              focus={props.focus}
              assetUrl={props.assetUrl}
            />
          );
        case "ia.progress@1":
          return <ProgressContent el={el} doc={props.doc} mode={props.mode} host={props.host} />;
        case "ia.countdown@1":
          return <CountdownContent el={el} doc={props.doc} mode={props.mode} host={props.host} />;
        default:
          return props.mode === "editor" ? (
            <div className="grid h-full w-full place-items-center border border-dashed border-current/40 text-[10px] opacity-70">
              Componente
            </div>
          ) : null;
      }
  }
}

/**
 * A layer nested in a group or a carousel slide, positioned relative to its
 * parent with its resolved presentation in this variant. Marked
 * `data-nested-id` (not `data-element-id`): on the canvas a click selects the
 * top-level layer that contains it.
 */
function Nested({ id, props, rc, fill }: { id: string; props: ArtboardProps; rc: RenderContext; fill?: boolean }) {
  const el = props.doc.elements.find((e) => e.id === id);
  const p = rc.resolved?.elements[id];
  if (!el || !p) return null;
  const shown = props.mode === "preview" ? (props.visibility?.get(id) ?? p.visible !== false) : true;
  if (!shown) return null;
  const css = elementCss(el, p, props.loadedFonts ?? EMPTY) as CSSProperties;
  const tappable = props.mode === "preview" && props.interact?.tappable(id) && !OWN_CONTROLS(el);
  const label = el.type === "button" ? undefined : (el.interactive?.label ?? el.name ?? "Abrir");
  return (
    <div
      data-nested-id={id}
      style={{
        ...css,
        ...(fill ? { left: 0, top: 0, width: "100%", height: "100%" } : {}),
        ...(props.mode === "editor" && p.visible === false ? { opacity: 0.35 } : {}),
        ...(tappable ? { cursor: "pointer" } : {}),
      }}
      {...(tappable
        ? {
            role: "button",
            tabIndex: 0,
            "aria-label": label,
            onClick: () => props.interact?.tap(id),
            onKeyDown: (e: React.KeyboardEvent) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                props.interact?.tap(id);
              }
            },
          }
        : {})}
    >
      {content({ element: el, presentation: p, frame: p.frame as unknown as Frame }, props, rc)}
    </div>
  );
}

/** Face-down card: the runtime draws the declared cover over the card (its face stays out of the a11y tree). */
function CardCover({ color, accent }: { color: string; accent?: string | undefined }) {
  return (
    <div
      aria-hidden="true"
      data-card-cover=""
      style={{
        position: "absolute",
        inset: 0,
        borderRadius: "inherit",
        background: color,
        boxShadow: accent ? `inset 0 0 0 3px ${accent}` : undefined,
        backgroundImage: accent
          ? `repeating-linear-gradient(45deg, transparent 0 6px, ${accent}33 6px 8px)`
          : undefined,
      }}
    />
  );
}

/**
 * Pointer drag of a draggable layer: the node follows the pointer (bounds and
 * axis from the engine) without React renders; on release the node is put back
 * where React drew it and the engine decides (placed, returned, dropped).
 */
function startDrag(
  e: React.PointerEvent<HTMLDivElement>,
  id: string,
  size: { width: number; height: number },
  interact: NonNullable<ArtboardProps["interact"]>
) {
  if (e.button !== 0 || !interact.dragStart?.(id)) return;
  const node = e.currentTarget;
  const board = node.parentElement;
  if (!board) return;
  e.preventDefault();
  const rect = board.getBoundingClientRect();
  const sx = rect.width / size.width || 1;
  const sy = rect.height / size.height || 1;
  const drawn = { left: node.style.left, top: node.style.top };
  const origin = { x: Number.parseFloat(drawn.left) || 0, y: Number.parseFloat(drawn.top) || 0 };
  const start = { x: e.clientX, y: e.clientY };
  let last = origin;
  node.setPointerCapture(e.pointerId);
  node.dataset.dragging = "true";
  const move = (ev: PointerEvent) => {
    const proposed = { x: origin.x + (ev.clientX - start.x) / sx, y: origin.y + (ev.clientY - start.y) / sy };
    last = interact.dragPosition?.(id, proposed.x, proposed.y) ?? proposed;
    node.style.left = `${String(last.x)}px`;
    node.style.top = `${String(last.y)}px`;
  };
  const end = () => {
    node.removeEventListener("pointermove", move);
    node.removeEventListener("pointerup", end);
    node.removeEventListener("pointercancel", end);
    delete node.dataset.dragging;
    node.style.left = drawn.left;
    node.style.top = drawn.top;
    interact.dragEnd?.(id, last.x, last.y);
  };
  node.addEventListener("pointermove", move);
  node.addEventListener("pointerup", end);
  node.addEventListener("pointercancel", end);
}

export function Artboard({ boardNode, ...props }: ArtboardProps) {
  const { doc, layoutId, mode } = props;
  const layout = findLayout(doc, layoutId);
  if (!layout) return null;
  const size = layoutSize(layout);
  const scene = editingScene(doc, props.sceneId);
  const assetByKey = new Map(doc.assets.map((a) => [a.key, a]));
  const editorHidden = props.editorHidden ?? EMPTY;
  const locked = props.locked ?? EMPTY;
  const bg = scene.background;
  const background: Css =
    bg?.kind === "image"
      ? (() => {
          const asset = assetByKey.get(bg.asset);
          return asset
            ? {
                backgroundImage: `url("${props.assetUrl(asset)}")`,
                backgroundSize: bg.fit === "fill" ? "100% 100%" : bg.fit,
                backgroundPosition: "center",
              }
            : {};
        })()
      : { background: bg ? fillCss(bg) : "transparent" };
  const views = variantViews(doc, layoutId, scene.id);
  const rc: RenderContext = { assetByKey, resolved: resolveVariant(doc, layoutId) };

  return (
    <div
      ref={boardNode}
      className={props.className}
      style={{
        position: "relative",
        width: size.width,
        height: size.height,
        overflow: "hidden",
        ...background,
      }}
      // A static preview is an image; an interactive one is a group of operable controls.
      role={mode === "preview" ? (props.interact ? "group" : "img") : undefined}
      aria-label={mode === "preview" ? doc.meta.accessibility.label : undefined}
      data-artboard={layoutId}
      data-scene={scene.id}
    >
      {views.map((view) => {
        const el = view.element;
        if (mode === "editor" && editorHidden.has(el.id)) return null;
        const runner = mode === "preview" ? props.runtime?.runner : undefined;
        // A spawn template is never drawn as itself while playing: its live instances use a fixed pool.
        const pool = runner?.spawns?.find((sp) => sp.templates.includes(el.id))?.maxActive;
        if (runner && el.template === true) {
          if (pool === undefined) return null;
          const css = elementCss(el, view.presentation, props.loadedFonts ?? EMPTY) as CSSProperties;
          return Array.from({ length: pool }, (_, slot) => (
            <div
              key={`${el.id}#${String(slot)}`}
              data-runner-slot={`${el.id}#${String(slot)}`}
              aria-hidden="true"
              style={{ ...css, display: "none" }}
            >
              {content(view, props, rc)}
            </div>
          ));
        }
        const loopCopy = runner?.layers?.some((l) => l.element === el.id && l.loop) ?? false;
        const control =
          runner?.controls?.jump === el.id ? "jump" : runner?.controls?.duck === el.id ? "duck" : undefined;
        const hiddenInAd = view.presentation.visible === false;
        const shown = props.visibility?.get(el.id) ?? !hiddenInAd;
        if (mode === "preview" && !shown) return null;
        const runtime = mode === "preview" ? props.runtime : undefined;
        const look = runtime ? runtimeLook(view.presentation, runtime.stateOf(el.id), runtime.positionOf(el.id)) : null;
        if (look?.hidden) return null;
        const p: ElementView["presentation"] =
          mode === "editor" && props.gesture?.id === el.id
            ? {
                ...view.presentation,
                frame: props.gesture.frame,
              }
            : (look?.presentation ?? view.presentation);
        const css = {
          ...(elementCss(el, p, props.loadedFonts ?? EMPTY) as CSSProperties),
          ...((look?.css ?? {}) as CSSProperties),
        };
        const game = runtime?.gameView(el.id) ?? null;
        const link = mode === "preview" && !props.interact ? linkOfElement(doc, el.id) : undefined;
        const drag = mode === "preview" && props.interact?.draggable?.(el.id) ? props.interact : undefined;
        const activate =
          mode === "preview" && props.interact?.tappable(el.id) && !OWN_CONTROLS(el)
            ? () => props.interact?.tap(el.id)
            : link && props.onActivateLink
              ? () => props.onActivateLink?.(link)
              : undefined;
        const hover = mode === "preview" && props.interact?.hoverable(el.id) ? props.interact : undefined;
        const baseLabel =
          el.type === "button"
            ? undefined
            : el.type === "hotspot"
              ? (el.interactive?.label ?? el.name ?? "Zona clicable")
              : (el.interactive?.label ?? el.name ?? "Abrir destino");
        const label = game
          ? gameLabel(el, baseLabel ?? el.interactive?.label ?? el.name ?? "Elemento", game, runtime?.picked ?? null)
          : baseLabel;
        const cover = game?.role === "card" && game.face === "down" ? props.doc.game?.pairs?.cover : undefined;
        const viewToDraw =
          look?.asset !== undefined && el.type === "image"
            ? { ...view, element: { ...el, asset: look.asset }, presentation: p }
            : { ...view, presentation: p };
        const keyboard = (e: React.KeyboardEvent, run: () => void) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            run();
          } else if (e.key === "Escape") props.interact?.cancelPick?.();
        };
        const node = (
          <div
            key={el.id}
            data-element-id={el.id}
            data-hidden-in-ad={hiddenInAd ? "true" : undefined}
            data-game-role={game?.role}
            data-game-state={
              game
                ? (game.face ?? (game.found ? "found" : game.placedIn ? "placed" : game.picked ? "picked" : ""))
                : undefined
            }
            style={{
              ...css,
              ...(mode === "editor" && hiddenInAd ? { opacity: 0.35 } : {}),
              ...(mode === "editor" && locked.has(el.id) ? { pointerEvents: "none" } : {}),
              ...(activate ? { cursor: "pointer" } : {}),
              ...(drag ? { cursor: "grab", touchAction: "none" } : {}),
              ...(game?.picked ? { outline: "3px solid #2563EB", outlineOffset: 2 } : {}),
            }}
            {...(drag
              ? {
                  role: "button",
                  tabIndex: 0,
                  "aria-label": label,
                  "aria-pressed": game?.picked ?? false,
                  onPointerDown: (e: React.PointerEvent<HTMLDivElement>) => {
                    startDrag(e, el.id, size, drag);
                  },
                  onKeyDown: (e: React.KeyboardEvent) => {
                    keyboard(e, () => drag.pick?.(el.id));
                  },
                }
              : activate
                ? {
                    role: "button",
                    tabIndex: el.interactive?.focusable === false ? -1 : 0,
                    "aria-label": label,
                    onClick: activate,
                    onKeyDown: (e: React.KeyboardEvent) => {
                      keyboard(e, activate);
                    },
                  }
                : game && label
                  ? { role: "img", "aria-label": label }
                  : {})}
            {...(control && activate
              ? {
                  // Press-and-hold controls: pointer down acts at once (a game cannot wait for the click).
                  onClick: undefined,
                  onPointerDown: (e: React.PointerEvent<HTMLDivElement>) => {
                    if (e.button !== 0) return;
                    e.preventDefault();
                    e.currentTarget.setPointerCapture(e.pointerId);
                    props.interact?.press?.(el.id, true);
                  },
                  onPointerUp: () => props.interact?.press?.(el.id, false),
                  onPointerCancel: () => props.interact?.press?.(el.id, false),
                  style: {
                    ...css,
                    cursor: "pointer",
                    touchAction: "none",
                    userSelect: "none",
                  },
                }
              : {})}
            {...(hover
              ? {
                  onPointerEnter: () => {
                    hover.hover(el.id, true);
                  },
                  onPointerLeave: () => {
                    hover.hover(el.id, false);
                  },
                }
              : {})}
          >
            {cover ? (
              <>
                <div aria-hidden="true" style={{ visibility: "hidden", display: "contents" }}>
                  {content(viewToDraw, props, rc)}
                </div>
                <CardCover color={cover.color} accent={cover.accent} />
              </>
            ) : (
              content(viewToDraw, props, rc)
            )}
          </div>
        );
        if (!loopCopy) return node;
        // A looping layer draws a copy right after itself (seamless repeat); the painter moves both.
        return (
          <Fragment key={el.id}>
            {node}
            <div data-runner-copy={el.id} aria-hidden="true" style={{ ...css, pointerEvents: "none" }}>
              {content(viewToDraw, props, rc)}
            </div>
          </Fragment>
        );
      })}
    </div>
  );
}
